import type { Sale, SaleInstallment, Vehicle } from '../types'
import { isConsignmentCommissionFixed } from './consignmentCommission'

/** Converte valor para número (evita concatenação de strings nos cálculos). */
const toNum = (v: unknown): number => {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  const s = String(v).trim()
  if (s === '') return 0
  if (/,\d*$/.test(s)) return Number(s.replace(/\./g, '').replace(',', '.')) || 0
  return parseFloat(s) || 0
}

type SaleWithVehicle = Pick<Sale, 'sale_price'> & { vehicle?: Vehicle | null }

type SaleForCashProfit = SaleWithVehicle &
  Pick<Sale, 'sale_date' | 'payment_method' | 'down_payment' | 'payment_status' | 'installments'>

export type CashRecognitionEvent = { date: string; amount: number }

const normalizeDate = (dateStr?: string | null): string =>
  dateStr ? String(dateStr).split('T')[0] : ''

/**
 * Receita da empresa na venda (consignado = comissão; direto = valor da venda).
 */
export function getCompanyRevenueFromSale(sale: SaleWithVehicle): number {
  const vehicle = sale.vehicle
  const salePrice = toNum(sale.sale_price)

  if (vehicle?.is_consignado && vehicle.consignment_commission != null) {
    return isConsignmentCommissionFixed(vehicle.consignment_commission_type)
      ? toNum(vehicle.consignment_commission)
      : salePrice * (toNum(vehicle.consignment_commission) / 100)
  }

  return salePrice
}

/**
 * Valor já recebido em venda a prazo (entrada + parcelas com baixa).
 * Vendas à vista ou com outro método não usam esta função diretamente.
 */
export function getTermSaleAmountReceived(
  sale: Pick<Sale, 'sale_price' | 'down_payment' | 'payment_status' | 'installments'>
): number {
  const salePrice = toNum(sale.sale_price)
  if (salePrice <= 0) return 0

  if (sale.payment_status === 'paid') return salePrice

  const downPayment = toNum(sale.down_payment)
  let received = downPayment

  const installments = sale.installments ?? []
  if (installments.length > 0) {
    received += installments
      .filter((i) => i.status === 'paid')
      .reduce((sum, i) => sum + toNum(i.amount), 0)
  }

  return Math.min(received, salePrice)
}

/**
 * Lucro reconhecido no caixa para uma venda.
 * À vista: lucro integral na data da venda.
 * A prazo: lucro proporcional ao valor já recebido (entrada + parcelas pagas).
 */
export type TermProfitBreakdownRow = {
  key: string
  label: string
  sequence?: number
  paymentAmount: number
  profitAmount: number
  status: 'paid' | 'pending'
  paidAt?: string | null
  dueDate?: string
}

export type TermProfitBreakdown = {
  totalProfit: number
  recognizedProfit: number
  pendingProfit: number
  rows: TermProfitBreakdownRow[]
}

/**
 * Discrimina o lucro da venda a prazo por entrada e parcelas (reconhecido vs. pendente).
 */
export function getTermSaleProfitBreakdown(
  sale: SaleForCashProfit,
  installments: SaleInstallment[] = sale.installments ?? []
): TermProfitBreakdown | null {
  if (sale.payment_method !== 'term') return null

  const salePrice = toNum(sale.sale_price)
  const totalProfit = getRealProfitFromSale(sale)
  if (salePrice <= 0) {
    return { totalProfit, recognizedProfit: 0, pendingProfit: totalProfit, rows: [] }
  }

  const ratio = totalProfit / salePrice
  const fullyPaid = sale.payment_status === 'paid'
  const rows: TermProfitBreakdownRow[] = []

  const downPayment = toNum(sale.down_payment)
  if (downPayment > 0) {
    rows.push({
      key: 'down_payment',
      label: 'Entrada',
      paymentAmount: downPayment,
      profitAmount: downPayment * ratio,
      status: 'paid',
      paidAt: sale.sale_date,
    })
  }

  for (const installment of [...installments].sort((a, b) => a.sequence - b.sequence)) {
    const paymentAmount = toNum(installment.amount)
    if (paymentAmount <= 0) continue

    rows.push({
      key: `installment-${installment.id}`,
      label: `Parcela ${installment.sequence}`,
      sequence: installment.sequence,
      paymentAmount,
      profitAmount: paymentAmount * ratio,
      status: fullyPaid || installment.status === 'paid' ? 'paid' : 'pending',
      paidAt: installment.paid_at,
      dueDate: installment.due_date,
    })
  }

  const recognizedProfit = rows
    .filter((row) => row.status === 'paid')
    .reduce((sum, row) => sum + row.profitAmount, 0)

  return {
    totalProfit,
    recognizedProfit,
    pendingProfit: totalProfit - recognizedProfit,
    rows,
  }
}

export function getCashRecognizedProfitFromSale(sale: SaleForCashProfit): number {
  const realProfit = getRealProfitFromSale(sale)
  const salePrice = toNum(sale.sale_price)

  if (sale.payment_method !== 'term' || salePrice <= 0) {
    return realProfit
  }

  const received = getTermSaleAmountReceived(sale)
  if (received <= 0) return 0

  return realProfit * (received / salePrice)
}

/** Pagamentos recebidos com data (entrada na venda; parcelas na data da baixa). */
export function getCashPaymentEventsFromSale(
  sale: Pick<Sale, 'sale_date' | 'sale_price' | 'payment_method' | 'down_payment' | 'payment_status' | 'installments'>
): CashRecognitionEvent[] {
  const salePrice = toNum(sale.sale_price)
  if (salePrice <= 0) return []

  const saleDate = normalizeDate(sale.sale_date) || new Date().toISOString().split('T')[0]

  if (sale.payment_method !== 'term') {
    return [{ date: saleDate, amount: salePrice }]
  }

  if (sale.payment_status === 'paid') {
    return [{ date: saleDate, amount: salePrice }]
  }

  const events: CashRecognitionEvent[] = []
  const downPayment = toNum(sale.down_payment)
  if (downPayment > 0) {
    events.push({ date: saleDate, amount: downPayment })
  }

  for (const installment of sale.installments ?? []) {
    if (installment.status !== 'paid') continue
    const amount = toNum(installment.amount)
    if (amount <= 0) continue
    events.push({
      date: normalizeDate(installment.paid_at) || saleDate,
      amount,
    })
  }

  return events
}

/** Lucro reconhecido por data de recebimento (regime de caixa). */
export function getCashProfitEventsFromSale(sale: SaleForCashProfit): CashRecognitionEvent[] {
  const salePrice = toNum(sale.sale_price)
  const realProfit = getRealProfitFromSale(sale)
  if (salePrice <= 0 || realProfit === 0) return []

  const ratio = realProfit / salePrice
  return getCashPaymentEventsFromSale(sale).map((event) => ({
    date: event.date,
    amount: event.amount * ratio,
  }))
}

/** Receita da empresa reconhecida por data de recebimento (regime de caixa). */
export function getCashRevenueEventsFromSale(sale: SaleForCashProfit): CashRecognitionEvent[] {
  const salePrice = toNum(sale.sale_price)
  const companyRevenue = getCompanyRevenueFromSale(sale)
  if (salePrice <= 0 || companyRevenue === 0) return []

  const ratio = companyRevenue / salePrice
  return getCashPaymentEventsFromSale(sale).map((event) => ({
    date: event.date,
    amount: event.amount * ratio,
  }))
}

export function isDateWithinRange(dateStr: string, from: string, to: string): boolean {
  const d = normalizeDate(dateStr)
  return d >= from && d <= to
}

export function sumCashEventsInRange(
  events: CashRecognitionEvent[],
  from: string,
  to: string
): number {
  return events
    .filter((event) => isDateWithinRange(event.date, from, to))
    .reduce((sum, event) => sum + event.amount, 0)
}

export function sumCashProfitInRange(sales: SaleForCashProfit[], from: string, to: string): number {
  return sales.reduce(
    (sum, sale) => sum + sumCashEventsInRange(getCashProfitEventsFromSale(sale), from, to),
    0
  )
}

export function sumCashRevenueInRange(sales: SaleForCashProfit[], from: string, to: string): number {
  return sales.reduce(
    (sum, sale) => sum + sumCashEventsInRange(getCashRevenueEventsFromSale(sale), from, to),
    0
  )
}

/** Lucro reconhecido no período para uma venda (DRE / relatórios). */
export function getCashProfitInRangeForSale(sale: SaleForCashProfit, from: string, to: string): number {
  return sumCashEventsInRange(getCashProfitEventsFromSale(sale), from, to)
}

/** Agrupa lucro reconhecido por mês (YYYY-MM). */
export function sumCashRecognizedProfit(sales: SaleForCashProfit[]): number {
  return sales.reduce((sum, sale) => sum + getCashRecognizedProfitFromSale(sale), 0)
}

export function buildProfitByMonthFromSales(
  sales: SaleForCashProfit[]
): Array<{ month: string; profit: number }> {
  const byMonth: Record<string, number> = {}

  for (const sale of sales) {
    for (const event of getCashProfitEventsFromSale(sale)) {
      const month = normalizeDate(event.date).slice(0, 7)
      if (!month) continue
      byMonth[month] = (byMonth[month] ?? 0) + event.amount
    }
  }

  return Object.entries(byMonth)
    .map(([month, profit]) => ({ month, profit }))
    .sort((a, b) => a.month.localeCompare(b.month))
}

/** Lucro reconhecido agrupado por tipo de veículo. */
export function buildProfitByVehicleTypeFromSales(
  sales: SaleForCashProfit[]
): Array<{ vehicleType: string; averageProfit: number; count: number }> {
  const byType: Record<string, { total: number; count: number }> = {}

  for (const sale of sales) {
    const vehicleType = sale.vehicle?.vehicleType?.trim() || 'Outros'
    const profit = getCashRecognizedProfitFromSale(sale)
    if (!byType[vehicleType]) byType[vehicleType] = { total: 0, count: 0 }
    byType[vehicleType].total += profit
    byType[vehicleType].count += 1
  }

  return Object.entries(byType).map(([vehicleType, data]) => ({
    vehicleType,
    averageProfit: data.count > 0 ? data.total / data.count : 0,
    count: data.count,
  }))
}

/**
 * Lucro real da operação na venda (mesma regra da lista de vendas / dashboard).
 * Direto: preço de venda − compra − despesas do veículo.
 * Consignado: comissão − despesas do veículo.
 */
export function getRealProfitFromSale(sale: SaleWithVehicle): number {
  const vehicle = sale.vehicle
  const salePrice = toNum(sale.sale_price)
  const totalExpenses =
    vehicle?.expenses?.reduce((expenseSum, expense) => expenseSum + toNum(expense.amount), 0) ?? 0
  const purchasePrice = toNum(vehicle?.purchase_price)

  if (vehicle?.is_consignado) {
    const commissionValue = isConsignmentCommissionFixed(vehicle.consignment_commission_type)
      ? toNum(vehicle.consignment_commission)
      : salePrice * (toNum(vehicle.consignment_commission) / 100)
    return commissionValue - totalExpenses
  }
  return salePrice - purchasePrice - totalExpenses
}
